import React, { useState, useEffect } from 'react';
import {
  Play, ChevronRight, Eye, AlertCircle, Check, X,
  ArrowUp, Shield, Zap, Copy,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AppLayout } from '../components/AppLayout';
import { PageHeader } from '../components/shared';
import { demoService } from '../services';
import { toast } from 'sonner';

import { getScoreBandClass, getScoreBandColor, getScoreBandLabel } from '../design-system/tokens';
import { DIMENSIONS } from '../design-system/content';

function DemoRunDetail({ run }) {
  if (!run) return null;

  const dims = Object.entries(run.dimension_scores || {}).map(([key, score]) => ({
    key,
    label: DIMENSIONS[key]?.label || key,
    score,
  }));

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  return (
    <div className="space-y-5 max-h-[75vh] overflow-y-auto pr-1" data-testid="demo-run-detail">
      {/* Score hero */}
      <div className="text-center msml-panel p-5">
        <p className="text-xs text-[var(--msml-text-muted)] uppercase tracking-wider mb-1">HQ Score</p>
        <p className={`text-4xl font-bold ${getScoreBandClass(run.hq_score)}`}>{run.hq_score?.toFixed(1)}</p>
        <p className="text-sm mt-1" style={{ color: getScoreBandColor(run.hq_score) }}>{getScoreBandLabel(run.hq_score)}</p>
        {run.hq_score_raw != null && run.hq_score_raw !== run.hq_score && (
          <p className="text-xs text-[var(--msml-text-muted)] mt-1">Raw: {run.hq_score_raw?.toFixed(1)}</p>
        )}
        {run.caps_applied?.length > 0 && (
          <div className="flex gap-1 justify-center mt-2 flex-wrap">
            {run.caps_applied.map((cap, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-[var(--msml-warning-bg)] text-[var(--msml-warning)]">
                <AlertCircle className="w-3 h-3" />{cap}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Input context */}
      <div className="msml-panel p-4">
        <p className="text-xs text-[var(--msml-text-muted)] mb-2 uppercase tracking-wider">User Prompt</p>
        <p className="text-sm text-white italic mb-3">"{run.input?.user_prompt}"</p>
        <p className="text-xs text-[var(--msml-text-muted)] mb-1 uppercase tracking-wider">AI Response (Original)</p>
        <p className="text-sm text-[var(--msml-text-secondary)] leading-relaxed">{run.input?.original_text}</p>
        <div className="flex gap-3 mt-2 text-[10px] text-[var(--msml-text-muted)]">
          {run.input?.domain && <span>Domain: {run.input.domain}</span>}
          {run.input?.audience && <span>Audience: {run.input.audience}</span>}
          {run.input?.sensitivity && <span>Sensitivity: {run.input.sensitivity}</span>}
        </div>
      </div>

      {/* Dimension scores */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {dims.map(d => {
          const scoreColor = d.score >= 70 ? 'var(--msml-success)' : d.score >= 50 ? 'var(--msml-warning)' : 'var(--msml-danger)';
          return (
            <div key={d.key} className="msml-panel p-3">
              <p className="text-[10px] text-[var(--msml-text-muted)] uppercase tracking-wider">{d.label}</p>
              <p className="text-lg font-bold" style={{ color: scoreColor }}>{d.score?.toFixed(1)}</p>
              <div className="h-1 bg-white/10 rounded-full mt-1 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${d.score}%`, backgroundColor: scoreColor }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Drivers */}
      {run.drivers?.length > 0 && (
        <div className="msml-panel p-4">
          <p className="text-xs text-[var(--msml-text-muted)] mb-2 uppercase tracking-wider">Score Drivers</p>
          <div className="space-y-2">
            {run.drivers.map((d, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${d.impact > 0 ? 'bg-[var(--msml-success)]' : 'bg-[var(--msml-danger)]'}`} />
                <span>
                  <span className="text-[var(--msml-text-muted)] capitalize">{d.dimension}: </span>
                  <span className="text-[var(--msml-text-secondary)]">{d.reason}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Evidence */}
      {run.evidence?.length > 0 && (
        <div className="msml-panel p-4">
          <p className="text-xs text-[var(--msml-text-muted)] mb-2 uppercase tracking-wider">Evidence Spans</p>
          <div className="space-y-1.5">
            {run.evidence.map((e, i) => (
              <div key={i} className="flex items-start gap-2 text-[10px]">
                <span className={`shrink-0 px-1 rounded ${
                  e.type === 'positive' ? 'bg-[var(--msml-success-bg)] text-[var(--msml-success)]'
                  : e.type === 'risk' ? 'bg-[var(--msml-warning-bg)] text-[var(--msml-warning)]'
                  : 'bg-[var(--msml-danger-bg)] text-[var(--msml-danger)]'
                }`}>{e.dimension}</span>
                <span className="text-[var(--msml-text-secondary)] italic">"{e.quote}"</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flags */}
      {(run.flags?.high_risk || run.flags?.high_dependency || run.flags?.overload) && (
        <div className="msml-panel p-3 border-l-2 border-[var(--msml-warning)]">
          <p className="text-xs text-[var(--msml-warning)] font-medium mb-1">Flags Triggered</p>
          <div className="flex gap-2 flex-wrap">
            {run.flags?.high_risk && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--msml-danger-bg)] text-[var(--msml-danger)]">High Risk</span>}
            {run.flags?.high_dependency && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--msml-warning-bg)] text-[var(--msml-warning)]">High Dependency</span>}
            {run.flags?.overload && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--msml-warning-bg)] text-[var(--msml-warning)]">Overload</span>}
          </div>
        </div>
      )}

      {/* Optimization */}
      {run.optimization && (
        <div className="msml-panel p-4 border-l-2 border-[var(--msml-success)]">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-[var(--msml-success)] uppercase tracking-wider flex items-center gap-1">
              <Zap className="w-3 h-3" /> Optimized Rewrite
            </p>
            <button onClick={() => handleCopy(run.optimization.optimized_text)} className="text-[var(--msml-text-muted)] hover:text-white">
              <Copy className="w-3 h-3" />
            </button>
          </div>
          <p className="text-sm text-[var(--msml-text-secondary)] leading-relaxed mb-3">{run.optimization.optimized_text}</p>
          {run.optimization.improvement_summary && (
            <p className="text-xs text-[var(--msml-text-muted)] italic">{run.optimization.improvement_summary}</p>
          )}
          {run.optimization.improvements?.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {run.optimization.improvements.map((imp, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <ArrowUp className="w-3 h-3 text-[var(--msml-success)]" />
                  <span className="text-[var(--msml-text-muted)] capitalize">{imp.dimension}: </span>
                  <span className="text-[var(--msml-text-secondary)]">{imp.change}</span>
                  <span className="text-[var(--msml-success)]">+{imp.delta}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Judge */}
      {run.judge && (
        <div className="msml-panel p-4">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4 text-[var(--msml-text-muted)]" />
            <p className="text-xs text-[var(--msml-text-muted)] uppercase tracking-wider">Judge Verdict</p>
          </div>
          <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
            run.judge.overall_verdict === 'APPROVED' ? 'bg-[var(--msml-success-bg)] text-[var(--msml-success)]'
            : run.judge.overall_verdict === 'REJECTED' ? 'bg-[var(--msml-danger-bg)] text-[var(--msml-danger)]'
            : 'bg-[var(--msml-warning-bg)] text-[var(--msml-warning)]'
          }`}>
            {run.judge.overall_verdict === 'APPROVED' ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
            {run.judge.overall_verdict}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
            <div>
              <span className="text-[var(--msml-text-muted)]">Intent: </span>
              <span className={run.judge.intent_status === 'pass' ? 'text-[var(--msml-success)]' : 'text-[var(--msml-danger)]'}>{run.judge.intent_status} ({Math.round((run.judge.intent_similarity || 0) * 100)}%)</span>
            </div>
            <div>
              <span className="text-[var(--msml-text-muted)]">Safety: </span>
              <span className={run.judge.safety_status === 'pass' ? 'text-[var(--msml-success)]' : 'text-[var(--msml-warning)]'}>{run.judge.safety_status}</span>
            </div>
          </div>
          {run.judge.notes && <p className="text-xs text-[var(--msml-text-muted)] mt-2">{run.judge.notes}</p>}
        </div>
      )}

      {/* Trace */}
      {run.trace && (
        <div className="msml-panel p-3">
          <p className="text-xs text-[var(--msml-text-muted)] mb-1 uppercase tracking-wider">Determinism Trace</p>
          <div className="grid grid-cols-2 gap-1 text-[10px] text-[var(--msml-text-muted)]">
            <span>Rubric: {run.trace.rubric_version}</span>
            <span>Prompt: {run.trace.prompt_version}</span>
            <span>Scorer: {run.trace.scoring_model_id}</span>
            <span>Status: {run.trace.status}</span>
            <span className="col-span-2 font-mono break-all">Hash: {run.determinism_hash}</span>
          </div>
        </div>
      )}
    </div>
  );
}

const BAND_COLORS = {
  poor: 'var(--msml-danger)',
  acceptable: 'var(--msml-warning)',
  good: 'var(--msml-primary)',
  excellent: 'var(--msml-success)',
};

export default function DemoRunsPage() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState(null);

  useEffect(() => { loadRuns(); }, []);

  const loadRuns = async () => {
    setLoading(true);
    try {
      const data = await demoService.getRuns();
      setRuns(data.items || []);
    } catch (err) {
      console.error('Failed to load demo runs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout activeNav="demo">
      <div className="p-4 lg:p-6">
        <div className="max-w-5xl mx-auto">
          <PageHeader
            icon={<Eye />}
            title="Demo Showcase"
            subtitle="Pre-computed analysis runs across diverse real-world scenarios — fully deterministic (same input → same scores + hash). Click any run to inspect the full pipeline."
            actions={
              <span className="liquid-pill liquid-pill--warning" data-testid="demo-badge">
                Demo
              </span>
            }
          />

          {loading ? (
            <div className="flex justify-center py-16"><span className="msml-spinner" /></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="demo-gallery">
              {runs.map(run => {
                const bandColor = run.hq_score >= 85 ? BAND_COLORS.excellent
                  : run.hq_score >= 70 ? BAND_COLORS.good
                  : run.hq_score >= 50 ? BAND_COLORS.acceptable
                  : BAND_COLORS.poor;
                const hasOpt = !!run.optimization;
                const hasJudge = !!run.judge;

                return (
                  <div
                    key={run.run_id}
                    className="liquid-card liquid-lift p-5 cursor-pointer relative overflow-hidden group"
                    onClick={() => setSelectedRun(run)}
                    data-testid={`demo-run-${run.run_id}`}
                  >
                    {/* Score glow backing */}
                    <div
                      className="absolute -top-12 -right-12 w-36 h-36 rounded-full pointer-events-none opacity-[0.15] group-hover:opacity-30 transition-opacity"
                      style={{ background: `radial-gradient(circle, ${bandColor} 0%, transparent 70%)` }}
                      aria-hidden
                    />
                    <div className="flex items-start gap-4 relative z-10">
                      {/* Score */}
                      <div className="text-center shrink-0">
                        <div
                          className="text-4xl font-light tracking-tighter"
                          style={{ color: bandColor, fontFamily: 'Outfit, sans-serif' }}
                        >
                          {run.hq_score?.toFixed(1)}
                        </div>
                        <p className="text-[10px] text-ink-muted uppercase tracking-[0.18em] mt-0.5">{getScoreBandLabel(run.hq_score)}</p>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                          <h3 className="text-sm font-semibold text-ink">{run.demo_label}</h3>
                          {run.caps_applied?.length > 0 && (
                            <span className="liquid-pill liquid-pill--warning !py-0.5 !px-2 !text-[10px]">
                              {run.caps_applied[0]}
                            </span>
                          )}
                          {run.flags?.high_risk && <span className="liquid-pill liquid-pill--danger !py-0.5 !px-2 !text-[10px]">High Risk</span>}
                          {run.flags?.high_dependency && <span className="liquid-pill liquid-pill--warning !py-0.5 !px-2 !text-[10px]">Dependency</span>}
                        </div>
                        <p className="text-xs text-ink-muted italic mb-2 line-clamp-1">"{run.input?.user_prompt}"</p>
                        <p className="text-xs text-ink-2 line-clamp-2 leading-relaxed">{run.input?.original_text}</p>
                        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                          {hasOpt && <span className="liquid-pill liquid-pill--success !py-0.5 !px-2 !text-[10px]"><Zap className="w-3 h-3" /> Optimized</span>}
                          {hasJudge && <span className="liquid-pill liquid-pill--info !py-0.5 !px-2 !text-[10px]"><Shield className="w-3 h-3" /> {run.judge.overall_verdict}</span>}
                          <span className="text-[10px] text-ink-muted font-mono">{run.run_id}</span>
                        </div>
                      </div>

                      <ChevronRight className="w-5 h-5 text-ink-muted shrink-0 mt-1 group-hover:text-ink-accent transition-colors" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Detail modal */}
          <Dialog open={!!selectedRun} onOpenChange={() => setSelectedRun(null)}>
            <DialogContent className="bg-[var(--msml-panel)] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white">{selectedRun?.demo_label}</DialogTitle>
              </DialogHeader>
              <DemoRunDetail run={selectedRun} />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </AppLayout>
  );
}
